import type { Span, Tracer, Counter, Histogram, Meter } from '@opentelemetry/api'
import { trace, SpanStatusCode, metrics } from '@opentelemetry/api'
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web'
import { TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { resourceFromAttributes, defaultResource } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'

import type { IWebTelemetryService, WebTelemetryConfig } from '@ztechapps/common.contract'
import { hasWindow, getWindow } from '@ztechapps/common.utils'
import { SessionManager } from './SessionManager'
import { DEFAULT_CONFIG, METRIC_PREFIX, SPAN_PREFIX, deriveEnvironmentFromHostname } from '../config/WebTelemetryConfig'

/**
 * Browser-based OpenTelemetry service for frontend telemetry
 *
 * Provides distributed tracing, metrics collection, and user analytics
 * to replace Google Analytics with OpenTelemetry.
 */
export class WebTelemetryService implements IWebTelemetryService {
  private tracer: Tracer
  private meter: Meter
  private sessionManager: SessionManager
  private config: WebTelemetryConfig
  private tracerProvider: WebTracerProvider
  private meterProvider: MeterProvider

  // Metric caches
  private histograms = new Map<string, Histogram>()
  private counters = new Map<string, Counter>()

  // Pre-created common metrics
  private pageViewCounter: Counter
  private eventCounter: Counter
  private errorCounter: Counter

  constructor(config: WebTelemetryConfig) {
    // Merge with defaults
    const mergedConfig = { ...DEFAULT_CONFIG, ...config }

    // Derive environment from hostname for resource attributes
    const environment = deriveEnvironmentFromHostname()

    this.config = {
      ...mergedConfig,
      environment,
    } as WebTelemetryConfig & { environment: string }

    this.sessionManager = new SessionManager()

    if (!this.config.enabled || !hasWindow()) {
      // Create no-op implementations for SSR or disabled mode
      this.tracer = trace.getTracer('noop')
      this.meter = metrics.getMeter('noop')
      this.pageViewCounter = this.meter.createCounter('noop')
      this.eventCounter = this.meter.createCounter('noop')
      this.errorCounter = this.meter.createCounter('noop')
      return
    }

    // Create resource with service info
    const resource = defaultResource().merge(
      resourceFromAttributes({
        [ATTR_SERVICE_NAME]: this.config.serviceName,
        [ATTR_SERVICE_VERSION]: this.config.serviceVersion || '1.0.0',
        'deployment.environment': environment,
        'browser.language': navigator.language,
        'browser.user_agent': navigator.userAgent,
      })
    )

    // Configure OTLP trace exporter
    const traceExporter = new OTLPTraceExporter({
      url: `${this.config.collectorUrl}/v1/traces`,
      headers: {},
    })

    // Initialize Trace Provider with span processor
    this.tracerProvider = new WebTracerProvider({
      resource,
      // Sample rate configuration
      sampler: this.config.sampleRate < 1.0 ? new TraceIdRatioBasedSampler(this.config.sampleRate) : undefined,
      spanProcessors: [
        new BatchSpanProcessor(traceExporter, {
          maxQueueSize: 100, // Limit queue size (default: 2048)
          maxExportBatchSize: 50, // Limit batch size (default: 512)
          scheduledDelayMillis: 15000, // Export every 30 seconds (default: 5000)
        }),
      ],
    })

    // Register with zone context for async context propagation
    this.tracerProvider.register({
      contextManager: new ZoneContextManager(),
    })

    // Initialize Meter Provider
    const metricExporter = new OTLPMetricExporter({
      url: `${this.config.collectorUrl}/v1/metrics`,
      headers: {},
    })

    this.meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter: metricExporter,
          exportIntervalMillis: 60000, // Export every 60 seconds (reduced from 10s)
        }),
      ],
    })

    metrics.setGlobalMeterProvider(this.meterProvider)

    // Get tracer and meter
    this.tracer = trace.getTracer(this.config.serviceName, this.config.serviceVersion)
    this.meter = metrics.getMeter(this.config.serviceName, this.config.serviceVersion)

    // Create common metrics
    this.pageViewCounter = this.meter.createCounter(`${METRIC_PREFIX}.page_views`, {
      description: 'Number of page views',
    })

    this.eventCounter = this.meter.createCounter(`${METRIC_PREFIX}.events`, {
      description: 'Number of custom events',
    })

    this.errorCounter = this.meter.createCounter(`${METRIC_PREFIX}.errors`, {
      description: 'Number of JavaScript errors',
    })

    // Register auto-instrumentations
    this.registerInstrumentations()

    // Setup global error handler
    this.setupErrorHandler()

    // Setup page unload flush
    this.setupUnloadHandler()

    if (this.config.debug) {
      console.info('[WebTelemetry] Initialized with config:', this.config)
    }
  }

  private registerInstrumentations(): void {
    registerInstrumentations({
      tracerProvider: this.tracerProvider,
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [/.*/], // Propagate to all URLs
          clearTimingResources: true,
          applyCustomAttributesOnSpan: (span, _request, _response) => {
            // Add session and user context to fetch spans
            const contextAttrs = this.sessionManager.getContextAttributes()
            for (const [key, value] of Object.entries(contextAttrs)) {
              span.setAttribute(key, value)
            }
          },
        }),
        new DocumentLoadInstrumentation(),
        new UserInteractionInstrumentation({
          eventNames: ['click', 'submit'],
        }),
      ],
    })
  }

  private setupErrorHandler(): void {
    getWindow().addEventListener('error', (event: ErrorEvent) => {
      this.recordError(event.error || new Error(event.message), {
        filename: event.filename,
        lineno: String(event.lineno),
        colno: String(event.colno),
      })
    })

    getWindow().addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
      this.recordError(error, { type: 'unhandledrejection' })
    })
  }

  private setupUnloadHandler(): void {
    getWindow().addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush()
      }
    })

    getWindow().addEventListener('pagehide', () => {
      this.flush()
    })
  }

  // === IWebTelemetryService Implementation ===

  startSpan<T>(
    name: string,
    fnOrAttributes: (() => T | Promise<T>) | Record<string, string>,
    fn?: () => T | Promise<T>
  ): T | Promise<T> {
    const spanName = `${SPAN_PREFIX}.${name}`
    const attributes = typeof fnOrAttributes === 'function' ? {} : fnOrAttributes
    const callback = typeof fnOrAttributes === 'function' ? fnOrAttributes : (fn ?? (() => undefined as T))

    // Add session/user context
    const contextAttrs = this.sessionManager.getContextAttributes()
    const allAttributes = { ...contextAttrs, ...attributes }

    return this.tracer.startActiveSpan(spanName, { attributes: allAttributes }, async (span: Span) => {
      try {
        const result = await callback()
        span.setStatus({ code: SpanStatusCode.OK })
        return result
      } catch (error) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : String(error),
        })
        if (error instanceof Error) {
          span.recordException(error)
        }
        throw error
      } finally {
        span.end()
      }
    })
  }

  recordHistogram(name: string, value: number, labels?: Record<string, string>): void {
    const metricName = `${METRIC_PREFIX}.${name}`

    let histogram = this.histograms.get(metricName)
    if (!histogram) {
      histogram = this.meter.createHistogram(metricName, {
        description: `Histogram for ${name}`,
      })
      this.histograms.set(metricName, histogram)
    }

    const contextAttrs = this.sessionManager.getContextAttributes()
    histogram.record(value, { ...contextAttrs, ...labels })
  }

  recordCounter(name: string, value: number, labels?: Record<string, string>): void {
    const metricName = `${METRIC_PREFIX}.${name}`

    let counter = this.counters.get(metricName)
    if (!counter) {
      counter = this.meter.createCounter(metricName, {
        description: `Counter for ${name}`,
      })
      this.counters.set(metricName, counter)
    }

    const contextAttrs = this.sessionManager.getContextAttributes()
    counter.add(value, { ...contextAttrs, ...labels })
  }

  trackPageView(pageName: string, attributes?: Record<string, string>): void {
    const contextAttrs = this.sessionManager.getContextAttributes()
    const allAttrs = {
      ...contextAttrs,
      'page.path': pageName,
      'page.url': hasWindow() ? getWindow().location.href : '',
      'page.title': hasWindow() ? getWindow().document.title : '',
      ...attributes,
    }

    this.pageViewCounter.add(1, allAttrs)

    // Also create a span for trace correlation
    this.tracer.startActiveSpan(`${SPAN_PREFIX}.page_view`, { attributes: allAttrs }, (span) => {
      span.setStatus({ code: SpanStatusCode.OK })
      span.end()
    })

    if (this.config.debug) {
      console.info('[WebTelemetry] Page view:', pageName, allAttrs)
    }
  }

  trackEvent(eventName: string, attributes?: Record<string, string>): void {
    const contextAttrs = this.sessionManager.getContextAttributes()
    const allAttrs = {
      ...contextAttrs,
      'event.name': eventName,
      ...attributes,
    }

    this.eventCounter.add(1, allAttrs)

    // Also create a span for trace correlation
    this.tracer.startActiveSpan(`${SPAN_PREFIX}.event.${eventName}`, { attributes: allAttrs }, (span) => {
      span.setStatus({ code: SpanStatusCode.OK })
      span.end()
    })

    if (this.config.debug) {
      console.info('[WebTelemetry] Event:', eventName, allAttrs)
    }
  }

  setUser(userId: string, attributes?: Record<string, string>): void {
    this.sessionManager.setUser(userId, attributes)

    if (this.config.debug) {
      console.info('[WebTelemetry] User set:', userId, attributes)
    }
  }

  clearUser(): void {
    this.sessionManager.clearUser()

    if (this.config.debug) {
      console.info('[WebTelemetry] User cleared')
    }
  }

  recordError(error: Error, attributes?: Record<string, string>): void {
    const contextAttrs = this.sessionManager.getContextAttributes()
    const errorAttrs = {
      ...contextAttrs,
      'error.name': error.name,
      'error.message': error.message,
      'page.url': hasWindow() ? getWindow().location.href : '',
      ...attributes,
    }

    this.errorCounter.add(1, errorAttrs)

    // Create error span
    this.tracer.startActiveSpan(`${SPAN_PREFIX}.error`, { attributes: errorAttrs }, (span) => {
      span.recordException(error)
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      })
      span.end()
    })

    if (this.config.debug) {
      console.info('[WebTelemetry] Error recorded:', error.message, errorAttrs)
    }
  }

  setGlobalAttribute(key: string, value: string): void {
    this.sessionManager.setCustomAttribute(key, value)

    if (this.config.debug) {
      console.info('[WebTelemetry] Global attribute set:', key, value)
    }
  }

  getSessionId(): string {
    return this.sessionManager.getSessionId()
  }

  getTraceContext(): Record<string, string> {
    const currentSpan = trace.getActiveSpan()
    if (!currentSpan) {
      return {}
    }

    const spanContext = currentSpan.spanContext()
    return {
      traceparent: `00-${spanContext.traceId}-${spanContext.spanId}-01`,
    }
  }

  async flush(): Promise<void> {
    try {
      await this.tracerProvider?.forceFlush()
      await this.meterProvider?.forceFlush()
      // Cleanup session manager event listeners
      this.sessionManager.destroy()
    } catch (error) {
      if (this.config.debug) {
        console.info('[WebTelemetry] Flush error:', error)
      }
    }
  }
}

/**
 * Factory function to create WebTelemetryService
 */
export function createWebTelemetryService(config: WebTelemetryConfig): IWebTelemetryService {
  return new WebTelemetryService(config)
}
